import React, { useCallback, useEffect, useState } from 'react';
import { Check, KeyRound, Send } from 'lucide-react';
import {
  Button,
  Card,
  Dot,
  Field,
  Input,
  Pill,
  SectionHeader,
  SegmentedControl,
  Skeleton,
  Switch,
} from '../../components/ui';
import type { MailSettings } from '../../types/notifications';
import { isAdminRole } from '../../features/intelligence/components/BriefPanel';
import { Notice, THUMB_REACH } from '../../features/notifications/components/ui';
import { checkSummary, deliverySummary } from '../../features/notifications/format';
import {
  notificationsErrorMessage,
  runDigest,
  saveMailSettings,
  sendTestMessage,
} from '../../features/notifications/notificationsApi';
import { useMailSettings } from '../../features/notifications/useNotifications';
import {
  RELAY_PRESETS,
  RelayPreset,
  presetForHost,
} from '../../features/notifications/relays';

interface Form {
  host: string;
  port: string;
  secure: boolean;
  username: string;
  fromName: string;
  fromAddress: string;
  appUrl: string;
  enabled: boolean;
}

const EMPTY: Form = {
  host: '',
  port: '587',
  secure: false,
  username: '',
  fromName: '',
  fromAddress: '',
  appUrl: '',
  enabled: false,
};

const toForm = (settings: MailSettings): Form => ({
  host: settings.host ?? '',
  port: String(settings.port ?? 587),
  secure: !!settings.secure,
  username: settings.username ?? '',
  fromName: settings.fromName ?? '',
  fromAddress: settings.fromAddress ?? '',
  appUrl: settings.appUrl ?? '',
  enabled: !!settings.enabled,
});

/** One short reason per field that is refused, keyed by field */
export function validateMail(form: Form): Partial<Record<keyof Form, string>> {
  const errors: Partial<Record<keyof Form, string>> = {};
  // Nothing is required until mail is actually switched on
  if (!form.enabled) return errors;
  if (!form.host.trim()) errors.host = 'Required';
  const port = Number(form.port);
  if (!form.port.trim() || Number.isNaN(port) || !Number.isInteger(port) || port < 1 || port > 65535)
    errors.port = 'A port between 1 and 65535';
  if (!form.fromAddress.trim()) errors.fromAddress = 'Required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.fromAddress.trim()))
    errors.fromAddress = 'Needs a full email address';
  if (form.appUrl.trim() && !/^https?:\/\//i.test(form.appUrl.trim()))
    errors.appUrl = 'Must start with http:// or https://';
  return errors;
}

const sameAsSaved = (form: Form, settings: MailSettings | null): boolean => {
  if (!settings) return false;
  const saved = toForm(settings);
  return (Object.keys(saved) as Array<keyof Form>).every((key) => saved[key] === form[key]);
};

/**
 * Where mail goes out from. Admin-only, and small enough to sit inside the
 * settings page rather than take one of its own.
 */
export const MailSettingsSection: React.FC<{ className?: string }> = ({ className = '' }) => {
  const admin = isAdminRole();
  const { settings, setSettings, loading, error: loadError, unavailable } = useMailSettings(admin);
  const [form, setForm] = useState<Form>(EMPTY);
  const [password, setPassword] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<{ ok: boolean; line: string } | null>(null);
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState<{ ok: boolean; line: string } | null>(null);

  useEffect(() => {
    if (settings) setForm(toForm(settings));
  }, [settings]);

  const errors = validateMail(form);
  const invalid = Object.keys(errors).length > 0;
  const dirty = !!settings && (!sameAsSaved(form, settings) || password !== null);

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const preset = presetForHost(form.host);

  /** Fills in what the provider documents, and leaves the credentials alone */
  const applyPreset = (option: RelayPreset) =>
    setForm((current) => ({
      ...current,
      host: option.host,
      port: String(option.port),
      secure: option.secure,
    }));

  const submit = useCallback(async () => {
    if (invalid) return;
    setSaving(true);
    setError(null);
    setTest(null);
    try {
      const payload: Partial<MailSettings> = {
        host: form.host.trim(),
        port: Number(form.port),
        secure: form.secure,
        username: form.username.trim(),
        fromName: form.fromName.trim(),
        fromAddress: form.fromAddress.trim(),
        appUrl: form.appUrl.trim(),
        enabled: form.enabled,
      };
      // The password only ever travels when someone has just typed one
      if (password !== null && password !== '') payload.password = password;
      const next = await saveMailSettings(payload);
      setSettings(next);
      setForm(toForm(next));
      setPassword(null);
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 2400);
    } catch (err) {
      setError(notificationsErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [form, invalid, password, setSettings]);

  const test_ = useCallback(async () => {
    setTesting(true);
    setTest(null);
    setError(null);
    try {
      const check = await sendTestMessage();
      setTest({ ok: check.ok, line: checkSummary(check) });
    } catch (err) {
      setTest({ ok: false, line: notificationsErrorMessage(err) });
    } finally {
      setTesting(false);
    }
  }, []);

  const digest = useCallback(async () => {
    setRunning(true);
    setRun(null);
    setError(null);
    try {
      const result = await runDigest('weekly_digest');
      setRun({ ok: !result.failed, line: deliverySummary(result) });
    } catch (err) {
      setRun({ ok: false, line: notificationsErrorMessage(err) });
    } finally {
      setRunning(false);
    }
  }, []);

  // These settings decide what the whole organisation can be told, and by whom
  if (!admin) return null;

  if (unavailable) {
    return (
      <section className={className}>
        <SectionHeader title="Email delivery" />
        <Card className="p-3">
          <Notice tone="quiet">
            Email delivery is not available on this server yet — the backend needs to be redeployed.
          </Notice>
        </Card>
      </section>
    );
  }

  const hasPassword = !!settings?.hasPassword;

  return (
    <section className={className}>
      <SectionHeader
        title="Email delivery"
        aside={
          dirty ? (
            <span className="inline-flex items-center gap-1.5">
              <Dot tone="red" /> Unsaved
            </span>
          ) : justSaved ? (
            <span className="inline-flex animate-fade-up items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> Saved
            </span>
          ) : undefined
        }
      />

      <Card divided>
        <div className="flex items-start justify-between gap-4 px-4 py-3.5">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] text-ink">Send email</p>
            <p className="mt-0.5 text-[12px] text-neutral-500">
              Off means nothing leaves the server, whatever anyone has switched on.
            </p>
          </div>
          <Switch
            className={`mt-0.5 ${THUMB_REACH}`}
            label="Send email"
            checked={form.enabled}
            disabled={loading}
            onChange={(next) => set('enabled', next)}
          />
        </div>

        <div className="p-4">
          {loading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-9 w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                label="Provider"
                hint={preset ? preset.sender : 'Pick one to fill in its settings, or enter them yourself.'}
                className="sm:col-span-2"
              >
                <div className="flex flex-wrap gap-1.5">
                  {RELAY_PRESETS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      aria-pressed={preset?.id === option.id}
                      onClick={() => applyPreset(option)}
                      className={`inline-flex h-8 items-center rounded-full px-3 text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
                        preset?.id === option.id
                          ? 'bg-ink text-white'
                          : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 hover:text-ink'
                      }`}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Host" htmlFor="mail-host" error={errors.host} className="sm:col-span-2">
                <Input
                  id="mail-host"
                  value={form.host}
                  placeholder="smtp.example.com"
                  autoComplete="off"
                  aria-invalid={!!errors.host}
                  onChange={(event) => set('host', event.target.value)}
                />
              </Field>

              <Field label="Port" htmlFor="mail-port" error={errors.port}>
                <Input
                  id="mail-port"
                  type="number"
                  inputMode="numeric"
                  className="tabular-nums"
                  value={form.port}
                  aria-invalid={!!errors.port}
                  onChange={(event) => set('port', event.target.value)}
                />
              </Field>

              <Field label="Encryption" hint={form.secure ? 'Encrypted from the first byte, usually port 465.' : 'Upgrades to TLS after connecting, usually port 587.'}>
                <SegmentedControl
                  label="Encryption"
                  value={form.secure ? 'tls' : 'starttls'}
                  onChange={(value) => set('secure', value === 'tls')}
                  items={[
                    { value: 'starttls', label: 'STARTTLS' },
                    { value: 'tls', label: 'TLS' },
                  ]}
                />
              </Field>

              <Field
                label="Username"
                htmlFor="mail-username"
                hint={preset ? preset.username : 'Empty means the server needs no sign-in.'}
              >
                <Input
                  id="mail-username"
                  value={form.username}
                  autoComplete="off"
                  onChange={(event) => set('username', event.target.value)}
                />
              </Field>

              <Field
                label="Password"
                htmlFor={password === null ? undefined : 'mail-password'}
                hint={
                  password === null
                    ? preset?.password
                    : (preset?.password ?? 'Saved write-only — it is never shown again.')
                }
              >
                {password === null ? (
                  <div className="flex h-10 items-center justify-between gap-2 rounded-xl px-3 ring-1 ring-inset ring-neutral-200 sm:h-9">
                    <span className="inline-flex min-w-0 items-center gap-2 text-[13px] text-neutral-500">
                      <KeyRound className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                      {hasPassword ? <Pill tone="green">Saved</Pill> : <span>None saved</span>}
                    </span>
                    <Button size="xs" variant="ghost" onClick={() => setPassword('')}>
                      {hasPassword ? 'Replace' : 'Set'}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      id="mail-password"
                      type="password"
                      value={password}
                      autoFocus
                      autoComplete="new-password"
                      placeholder="New password"
                      onChange={(event) => setPassword(event.target.value)}
                    />
                    <Button size="xs" variant="ghost" onClick={() => setPassword(null)}>
                      Cancel
                    </Button>
                  </div>
                )}
              </Field>

              <Field label="From name" htmlFor="mail-from-name">
                <Input
                  id="mail-from-name"
                  value={form.fromName}
                  placeholder="Staketory"
                  onChange={(event) => set('fromName', event.target.value)}
                />
              </Field>

              <Field label="From address" htmlFor="mail-from-address" error={errors.fromAddress}>
                <Input
                  id="mail-from-address"
                  type="email"
                  value={form.fromAddress}
                  placeholder="alerts@example.com"
                  aria-invalid={!!errors.fromAddress}
                  onChange={(event) => set('fromAddress', event.target.value)}
                />
              </Field>

              <Field
                label="App address"
                htmlFor="mail-app-url"
                error={errors.appUrl}
                hint={errors.appUrl ? undefined : 'Where the links in an email point.'}
                className="sm:col-span-2"
              >
                <Input
                  id="mail-app-url"
                  value={form.appUrl}
                  placeholder="https://staketory.example.com"
                  aria-invalid={!!errors.appUrl}
                  onChange={(event) => set('appUrl', event.target.value)}
                />
              </Field>
            </div>
          )}
        </div>
      </Card>

      {loadError && !unavailable && (
        <Notice className="mt-3" tone="error">
          {loadError}
        </Notice>
      )}
      {error && (
        <Notice className="mt-3" tone="error">
          {error}
        </Notice>
      )}
      {test && (
        <Notice className="mt-3" tone={test.ok ? 'ok' : 'error'}>
          {test.line}
        </Notice>
      )}
      {run && (
        <Notice className="mt-3" tone={run.ok ? 'ok' : 'error'}>
          {run.line}
        </Notice>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="ghost"
          loading={running}
          disabled={loading || dirty}
          title={dirty ? 'Save your changes first' : 'Sends the weekly brief to everyone due one, now'}
          onClick={digest}
        >
          Run the digest now
        </Button>
        <Button
          variant="secondary"
          loading={testing}
          disabled={loading || dirty}
          title={dirty ? 'Save your changes first' : 'Sends one message to your own address'}
          onClick={test_}
        >
          <Send className="h-4 w-4" strokeWidth={1.75} />
          Send test message
        </Button>
        <Button variant="primary" loading={saving} disabled={!dirty || invalid || loading} onClick={submit}>
          Save
        </Button>
      </div>
    </section>
  );
};

export default MailSettingsSection;
